import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "./context/CartContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WholesaleAuthProvider, useWholesaleAuth } from "./context/WholesaleAuthContext";
import { WholesaleCartProvider } from "./context/WholesaleCartContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import CartPage from "./pages/CartPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import WholesaleLoginPage from "./pages/WholesaleLoginPage";
import WholesaleProductsPage from "./pages/WholesaleProductsPage";
import WholesaleCartPage from "./pages/WholesaleCartPage";
import WholesaleOrdersPage from "./pages/WholesaleOrdersPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-maple-red border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/admin" replace />;
    }
    
    return children;
};

const WholesaleProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useWholesaleAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-maple-red border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/wholesale/login" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:productId" element={<ProductDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route 
                path="/admin/dashboard" 
                element={
                    <ProtectedRoute>
                        <AdminDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route path="/wholesale/login" element={<WholesaleLoginPage />} />
            <Route
                path="/wholesale/products"
                element={
                    <WholesaleProtectedRoute>
                        <WholesaleProductsPage />
                    </WholesaleProtectedRoute>
                }
            />
            <Route
                path="/wholesale/cart"
                element={
                    <WholesaleProtectedRoute>
                        <WholesaleCartPage />
                    </WholesaleProtectedRoute>
                }
            />
            <Route
                path="/wholesale/orders"
                element={
                    <WholesaleProtectedRoute>
                        <WholesaleOrdersPage />
                    </WholesaleProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <AuthProvider>
                    <WholesaleAuthProvider>
                        <WholesaleCartProvider>
                            <CartProvider>
                                <div className="App">
                                    <AppRoutes />
                                    <Toaster position="bottom-right" />
                                </div>
                            </CartProvider>
                        </WholesaleCartProvider>
                    </WholesaleAuthProvider>
                </AuthProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
